import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { portalCartAPI } from '../lib/portalApi';
import { usePortalAuth } from './PortalAuthContext';
import { toast } from 'sonner';

/**
 * Shared cart state for the B2B portal.
 *
 * Keeps a single source of truth for:
 *   - hydrated cart payload (items + totals + MOQ flags)
 *   - item_count (used by the layout badge)
 *   - mutation helpers (addItem / updateItem / removeItem / clear / submit)
 *
 * The provider is mounted inside `PortalAuthProvider` so a cart fetch is
 * tried as soon as we have a token. If the token is missing we don't touch
 * the network — keeps the public `/portal/login` route silent.
 */
const PortalCartContext = createContext(null);

const PORTAL_LINE_MOQ_DEFAULT = 6;

export const PortalCartProvider = ({ children }) => {
    const [cart, setCart] = useState(null);
    const [loading, setLoading] = useState(false);
    // Post-Wave-2 cookie migration: we no longer have direct visibility
    // into the auth token. Use the portal auth context's `profile` /
    // `loading` state to decide whether it's worth calling the cart API.
    const { profile, loading: authLoading } = usePortalAuth();
    const isAuthenticated = Boolean(profile);

    const refresh = useCallback(async () => {
        if (!isAuthenticated) {
            setCart(null);
            return null;
        }
        setLoading(true);
        try {
            const { data } = await portalCartAPI.get();
            setCart(data);
            return data;
        } catch {
            setCart(null);
            return null;
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated]);

    useEffect(() => {
        if (authLoading) return;
        refresh();
    }, [authLoading, refresh]);

    // Wrap each mutation so we keep `cart` fresh + show toast feedback.
    const addItem = useCallback(async (product_id, qty) => {
        try {
            const { data } = await portalCartAPI.addItem(product_id, qty);
            setCart(data);
            toast.success(`Added ${qty} to cart`);
            return data;
        } catch (e) {
            const msg = e.response?.data?.detail || 'Could not add to cart';
            toast.error(msg);
            throw e;
        }
    }, []);

    const updateItem = useCallback(async (product_id, qty) => {
        try {
            const { data } = await portalCartAPI.updateItem(product_id, qty);
            setCart(data);
            return data;
        } catch (e) {
            const msg = e.response?.data?.detail || 'Could not update item';
            toast.error(msg);
            throw e;
        }
    }, []);

    const removeItem = useCallback(async (product_id) => {
        try {
            const { data } = await portalCartAPI.removeItem(product_id);
            setCart(data);
            toast.success('Item removed');
            return data;
        } catch {
            toast.error('Could not remove item');
        }
    }, []);

    const clear = useCallback(async () => {
        try {
            const { data } = await portalCartAPI.clear();
            setCart(data);
            toast.success('Cart cleared');
        } catch {
            toast.error('Could not clear cart');
        }
    }, []);

    const submit = useCallback(async (notes, orderType = 'order') => {
        try {
            const { data } = await portalCartAPI.submit(notes, orderType);
            await refresh();
            return data;
        } catch (e) {
            const msg = e.response?.data?.detail || 'Could not submit order';
            toast.error(msg);
            throw e;
        }
    }, [refresh]);

    const value = {
        cart,
        loading,
        itemCount: cart?.item_count || 0,
        moq: cart?.moq_per_line || PORTAL_LINE_MOQ_DEFAULT,
        step: cart?.qty_step || cart?.moq_per_line || PORTAL_LINE_MOQ_DEFAULT,
        refresh,
        addItem,
        updateItem,
        removeItem,
        clear,
        submit,
    };

    return <PortalCartContext.Provider value={value}>{children}</PortalCartContext.Provider>;
};

export const usePortalCart = () => {
    const ctx = useContext(PortalCartContext);
    if (!ctx) throw new Error('usePortalCart must be used within PortalCartProvider');
    return ctx;
};
